import { useEffect, useState } from 'react';
import { send } from '@/shared/messages';
import type { Test, Chapter } from '@/shared/types';
import { TESTS_CHANGED_EVENT } from './testEvents';
import { Button } from './Button';
import { Alert } from './Alert';
import { Card } from './Card';
import { Input, Select } from './Field';

// Shared "Chapters" settings panel used by both the extension Options page
// and the webapp Settings page. Neither surface has the side panel's header
// TestPicker, so this panel carries its own test selector: pick a test, see
// its chapters, rename/delete them, and drag a row by its handle to reorder.
//
// Selecting a test here also persists it as the active test (SET_ACTIVE_TEST)
// so the rest of the app stays aligned with what you're managing. Reordering
// auto-persists: on drop we renumber the visible list 0..n-1 and PATCH each
// chapter whose order changed. Order is global in the schema, but every
// surface that reads it is test-scoped, so renumbering within a test is safe.
type ChapterRow = Chapter & { cardCount: number; dueCount: number };
type TestRow = Test & { chapterCount: number; dueCount: number };

export function ChaptersManager() {
  const [tests, setTests] = useState<TestRow[]>([]);
  const [selectedTestId, setSelectedTestId] = useState<string | null>(null);
  const [chapters, setChapters] = useState<ChapterRow[]>([]);
  const [loadingTests, setLoadingTests] = useState(true);
  const [loadingChapters, setLoadingChapters] = useState(false);
  const [titleEdits, setTitleEdits] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [reordering, setReordering] = useState(false);
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Load the test list and pick an initial selection: the active test if set,
  // else the first test. Runs on mount and on manual refresh.
  async function loadTests() {
    setLoadingTests(true);
    try {
      const [testsRes, act] = await Promise.all([
        send({ type: 'LIST_TESTS' }),
        send({ type: 'GET_ACTIVE_TEST' }),
      ]);
      setTests(testsRes.tests);
      setSelectedTestId((cur) => {
        if (cur && testsRes.tests.some((t) => t.id === cur)) return cur;
        const active = testsRes.tests.find((t) => t.id === act.activeTestId);
        return (active ?? testsRes.tests[0])?.id ?? null;
      });
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoadingTests(false);
    }
  }

  async function loadChapters(testId: string | null) {
    if (!testId) {
      setChapters([]);
      return;
    }
    setLoadingChapters(true);
    try {
      const r = await send({ type: 'LIST_CHAPTERS', testId });
      setChapters(r.chapters);
      setTitleEdits({});
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoadingChapters(false);
    }
  }

  useEffect(() => {
    void loadTests();
  }, []);

  // Refresh the test dropdown when the Tests panel creates/renames/deletes/
  // reorders a test.
  useEffect(() => {
    const onChanged = () => void loadTests();
    window.addEventListener(TESTS_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(TESTS_CHANGED_EVENT, onChanged);
  }, []);

  useEffect(() => {
    void loadChapters(selectedTestId);
  }, [selectedTestId]);

  function selectTest(id: string) {
    setSelectedTestId(id);
    // Keep the rest of the app aligned with what's being managed here.
    void send({ type: 'SET_ACTIVE_TEST', testId: id });
  }

  function titleDirty(c: ChapterRow): boolean {
    const v = titleEdits[c.id];
    return v !== undefined && v !== c.title;
  }

  async function saveTitle(c: ChapterRow) {
    const title = titleEdits[c.id];
    if (title === undefined || title === c.title) return;
    setBusyId(c.id);
    try {
      await send({ type: 'UPDATE_CHAPTER', chapterId: c.id, patch: { title } });
      await loadChapters(selectedTestId);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  }

  async function remove(c: ChapterRow) {
    if (
      !confirm(
        `Delete "${c.title}"? Removes ${c.cardCount} card(s), all sources, the summary, ` +
          `and Q&A history. This cannot be undone.`
      )
    ) {
      return;
    }
    setBusyId(c.id);
    try {
      await send({ type: 'DELETE_CHAPTER', chapterId: c.id });
      await loadChapters(selectedTestId);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  }

  // Move the chapter at `from` to index `to`, renumber 0..n-1, and persist
  // the rows whose order changed. Optimistic; refetch on failure. Shared by
  // drag-and-drop (desktop) and the ▲/▼ buttons (touch).
  async function reorderTo(from: number, to: number) {
    if (from < 0 || to < 0 || to >= chapters.length || from === to) return;
    const prev = chapters;
    const next = [...chapters];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    const renumbered = next.map((c, i) => ({ ...c, order: i }));
    setChapters(renumbered);

    setReordering(true);
    try {
      const writes = renumbered
        .filter((c) => prev.find((o) => o.id === c.id)?.order !== c.order)
        .map((c) => send({ type: 'UPDATE_CHAPTER', chapterId: c.id, patch: { order: c.order } }));
      await Promise.all(writes);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      await loadChapters(selectedTestId);
    } finally {
      setReordering(false);
    }
  }

  function dropOn(targetId: string) {
    const sourceId = dragId;
    setDragId(null);
    setOverId(null);
    if (!sourceId || sourceId === targetId) return;
    void reorderTo(
      chapters.findIndex((c) => c.id === sourceId),
      chapters.findIndex((c) => c.id === targetId)
    );
  }

  return (
    <Card className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">Chapters</h2>
        <div className="flex items-center gap-2">
          {reordering && <span className="text-xs text-slate-400">Saving order…</span>}
          <label className="flex items-center gap-1 text-xs text-slate-500">
            Test
            <Select
              size="sm"
              value={selectedTestId ?? ''}
              onChange={(e) => selectTest(e.target.value)}
              disabled={loadingTests || tests.length === 0}
              className="font-medium text-slate-800"
            >
              {tests.length === 0 && <option value="">no tests</option>}
              {tests.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.title} ({t.chapterCount})
                </option>
              ))}
            </Select>
          </label>
          <Button
            size="xs"
            onClick={() => {
              void loadTests();
              void loadChapters(selectedTestId);
            }}
          >
            Refresh
          </Button>
        </div>
      </div>
      <p className="text-xs text-slate-600">
        Reorder with the <span className="font-mono">▲▼</span> buttons (or drag the{' '}
        <span className="font-mono">⠿</span> handle on desktop). Rename inline, or delete a
        chapter (cards, sources, summary, and Q&amp;A history go with it).
      </p>
      {loadingTests || loadingChapters ? (
        <div className="text-xs text-slate-500">Loading…</div>
      ) : tests.length === 0 ? (
        <div className="rounded border border-dashed border-slate-300 p-3 text-xs text-slate-500">
          No tests yet.
        </div>
      ) : chapters.length === 0 ? (
        <div className="rounded border border-dashed border-slate-300 p-3 text-xs text-slate-500">
          No chapters in this test yet.
        </div>
      ) : (
        <div className="divide-y divide-slate-200">
          {chapters.map((c, idx) => {
            const titleVal = titleEdits[c.id] ?? c.title;
            const isDirty = titleDirty(c);
            return (
              <div
                key={c.id}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (overId !== c.id) setOverId(c.id);
                }}
                onDrop={() => dropOn(c.id)}
                className={`flex items-center gap-2 py-2 text-sm ${
                  overId === c.id && dragId && dragId !== c.id ? 'bg-blue-50' : ''
                } ${dragId === c.id ? 'opacity-50' : ''}`}
              >
                {/* Drag handle for desktop; hidden on touch where HTML5 DnD
                    doesn't fire — the ▲/▼ buttons cover reordering there. */}
                <span
                  draggable
                  onDragStart={() => setDragId(c.id)}
                  onDragEnd={() => {
                    setDragId(null);
                    setOverId(null);
                  }}
                  className="hidden cursor-grab select-none px-1 text-slate-400 hover:text-slate-600 active:cursor-grabbing @md:inline"
                  title="Drag to reorder"
                >
                  ⠿
                </span>
                <div className="flex shrink-0 flex-col">
                  <button
                    onClick={() => void reorderTo(idx, idx - 1)}
                    disabled={idx === 0 || reordering}
                    className="px-1 leading-none text-slate-400 hover:text-slate-700 disabled:opacity-30"
                    title="Move up"
                    type="button"
                  >
                    ▲
                  </button>
                  <button
                    onClick={() => void reorderTo(idx, idx + 1)}
                    disabled={idx === chapters.length - 1 || reordering}
                    className="px-1 leading-none text-slate-400 hover:text-slate-700 disabled:opacity-30"
                    title="Move down"
                    type="button"
                  >
                    ▼
                  </button>
                </div>
                <Input
                  type="text"
                  value={titleVal}
                  onChange={(ev) => setTitleEdits((m) => ({ ...m, [c.id]: ev.target.value }))}
                  className="min-w-0 flex-1"
                />
                <div className="w-14 shrink-0 text-right font-mono text-xs text-slate-500 @md:w-24">
                  {c.cardCount}c
                  {c.dueCount > 0 && <span className="ml-1 text-blue-700">·{c.dueCount}</span>}
                </div>
                <div className="flex shrink-0 justify-end gap-1 text-xs">
                  <Button
                    variant="primary"
                    size="xs"
                    className="min-h-[40px]"
                    onClick={() => void saveTitle(c)}
                    disabled={!isDirty || busyId === c.id}
                  >
                    Save
                  </Button>
                  <Button
                    variant="danger"
                    size="xs"
                    className="min-h-[40px]"
                    onClick={() => void remove(c)}
                    disabled={busyId === c.id}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {error && <Alert>{error}</Alert>}
    </Card>
  );
}
